import { SlideshowSelectEvent } from '@theme/events';

class DualBannerSlideshow extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #scrollButton = null;

  /** @type {((event: Event) => void) | null} */
  #handleScrollClick = null;

  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {ResizeObserver | null} */
  #fillViewportObserver = null;

  /** @type {MutationObserver | null} */
  #tabBarMountObserver = null;

  connectedCallback() {
    this.#slideshow = this.querySelector('slideshow-component');
    this.#setupAutoplayDuration();
    this.#setupFillViewportHeight();
    this.#setupScrollButton();
    this.#setupDotProgress();
  }

  disconnectedCallback() {
    this.#teardownFillViewportHeight();
    this.#teardownScrollButton();
    this.#teardownDotProgress();
  }

  #setupAutoplayDuration() {
    const speed = parseInt(this.dataset.autoplaySpeed ?? '', 10);
    const durationMs = Number.isFinite(speed) ? speed * 1000 : 5000;
    this.style.setProperty('--dual-banner-autoplay-duration', `${durationMs}ms`);
  }

  #setupFillViewportHeight() {
    if (this.dataset.fillViewport !== 'true') return;

    this.#updateFillViewportHeight();
    window.addEventListener('resize', this.#updateFillViewportHeight);

    this.#fillViewportObserver = new ResizeObserver(this.#updateFillViewportHeight);

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup instanceof HTMLElement) {
      this.#fillViewportObserver.observe(headerGroup);

      for (const child of headerGroup.children) {
        if (child instanceof HTMLElement) {
          this.#fillViewportObserver.observe(child);
        }
      }
    }

    this.#observeMobileTabBar();
  }

  #observeMobileTabBar() {
    const tabBar = document.querySelector('mobile-tab-bar.mobile-tab-bar');
    if (tabBar instanceof HTMLElement) {
      this.#fillViewportObserver?.observe(tabBar);
      this.#updateFillViewportHeight();
      return;
    }

    // Footer tab bar may mount after the homepage banner.
    this.#tabBarMountObserver = new MutationObserver(() => {
      const mountedTabBar = document.querySelector('mobile-tab-bar.mobile-tab-bar');
      if (!(mountedTabBar instanceof HTMLElement)) return;

      this.#fillViewportObserver?.observe(mountedTabBar);
      this.#updateFillViewportHeight();
      this.#tabBarMountObserver?.disconnect();
      this.#tabBarMountObserver = null;
    });

    this.#tabBarMountObserver.observe(document.body, { childList: true, subtree: true });
  }

  #teardownFillViewportHeight() {
    window.removeEventListener('resize', this.#updateFillViewportHeight);
    this.#fillViewportObserver?.disconnect();
    this.#fillViewportObserver = null;
    this.#tabBarMountObserver?.disconnect();
    this.#tabBarMountObserver = null;
  }

  #updateFillViewportHeight = () => {
    if (this.dataset.fillViewport !== 'true') return;

    const chromeHeight = measureHeaderChromeHeight();
    const tabBarOffset = measureMobileTabBarOffset();
    const viewportHeight = window.innerHeight;
    const fillHeight = Math.max(0, Math.round(viewportHeight - chromeHeight - tabBarOffset));

    this.style.setProperty('--dual-banner-chrome-height', `${chromeHeight}px`);
    this.style.setProperty('--dual-banner-tab-bar-offset', `${tabBarOffset}px`);
    this.style.setProperty('--dual-banner-fill-height', `${fillHeight}px`);
  };

  #setupScrollButton() {
    this.#scrollButton = this.querySelector('[data-dual-banner-scroll]');
    if (!this.#scrollButton) return;

    this.#handleScrollClick = (event) => {
      event.preventDefault();
      this.#scrollArrowCenterToHeaderBottom(this.#scrollButton);
    };

    this.#scrollButton.addEventListener('click', this.#handleScrollClick);
  }

  #teardownScrollButton() {
    if (this.#scrollButton && this.#handleScrollClick) {
      this.#scrollButton.removeEventListener('click', this.#handleScrollClick);
    }
  }

  #setupDotProgress() {
    if (!this.#slideshow) return;

    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#slideshow.addEventListener('mouseenter', this.#pauseDotProgress);
    this.#slideshow.addEventListener('mouseleave', this.#resumeDotProgress);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#restartActiveDotProgress();
  }

  #teardownDotProgress() {
    if (!this.#slideshow) return;

    this.#slideshow.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#slideshow.removeEventListener('mouseenter', this.#pauseDotProgress);
    this.#slideshow.removeEventListener('mouseleave', this.#resumeDotProgress);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const index = event.detail.index;
    const dot = this.querySelectorAll('.dual-banner__dot')[index];
    if (dot instanceof HTMLElement) {
      this.#restartDotProgress(dot);
    }
  };

  #pauseDotProgress = () => {
    this.classList.add('is-progress-paused');
  };

  #resumeDotProgress = () => {
    if (document.hidden) return;
    this.classList.remove('is-progress-paused');
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pauseDotProgress();
      return;
    }

    this.#resumeDotProgress();
  };

  #restartActiveDotProgress() {
    const activeDot = this.querySelector('.dual-banner__dot[aria-selected="true"]');
    if (activeDot instanceof HTMLElement) {
      this.#restartDotProgress(activeDot);
    }
  }

  /**
   * @param {HTMLElement} dot
   */
  #restartDotProgress(dot) {
    if (this.dataset.autoplay !== 'true') return;

    const progress = dot.querySelector('.dual-banner__dot-ring-progress');
    if (!(progress instanceof SVGCircleElement)) return;

    progress.style.animation = 'none';
    progress.getBoundingClientRect();
    progress.style.animation = '';
    this.classList.remove('is-progress-paused');
  }

  /**
   * @param {HTMLElement | null} arrow
   */
  #scrollArrowCenterToHeaderBottom(arrow) {
    if (!(arrow instanceof HTMLElement)) return;

    const arrowRect = arrow.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const arrowCenterY = arrowRect.top + scrollY + arrowRect.height / 2;
    const alignBottom = getScrollAlignBottom();
    const scrollTop = Math.max(0, arrowCenterY - alignBottom);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: scrollTop, behavior: prefersReducedMotion ? 'instant' : 'smooth' });
  }
}

if (!customElements.get('dual-banner-slideshow')) {
  customElements.define('dual-banner-slideshow', DualBannerSlideshow);
}

/**
 * Total height of announcement bar, header, and related header-group sections.
 * @returns {number}
 */
function measureHeaderChromeHeight() {
  const headerGroup = document.querySelector('#header-group');
  if (!(headerGroup instanceof HTMLElement)) {
    return getHeaderChromeHeightFromCss();
  }

  let total = 0;

  for (const child of headerGroup.children) {
    if (child instanceof HTMLElement) {
      total += child.offsetHeight;
    }
  }

  if (total > 0) return total;

  return getHeaderChromeHeightFromCss();
}

/**
 * Visible mobile tab bar height including safe-area padding. 0 when hidden/absent.
 * @returns {number}
 */
function measureMobileTabBarOffset() {
  const tabBar = document.querySelector('mobile-tab-bar.mobile-tab-bar');
  if (!(tabBar instanceof HTMLElement)) return 0;

  const styles = getComputedStyle(tabBar);
  if (styles.display === 'none' || styles.visibility === 'hidden') return 0;

  return tabBar.offsetHeight || 0;
}

/**
 * @returns {number}
 */
function getHeaderChromeHeightFromCss() {
  const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
  const announcementHeight = parseInt(
    getComputedStyle(document.body).getPropertyValue('--header-group-height'),
    10
  );

  const header = Number.isFinite(headerHeight) ? headerHeight : 0;
  const announcement = Number.isFinite(announcementHeight) ? announcementHeight : 0;

  return header + announcement;
}

/**
 * Viewport Y to align the arrow center with the sticky header bottom.
 * @returns {number}
 */
function getScrollAlignBottom() {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) {
    return getHeaderGroupBottomFromCss();
  }

  const headerRect = header.getBoundingClientRect();
  const stickyTopOffset = getStickyHeaderTopOffset();

  return headerRect.bottom - headerRect.top + stickyTopOffset;
}

/**
 * @returns {number}
 */
function getStickyHeaderTopOffset() {
  const headerSection = document.querySelector('#header-group .header-section');
  if (!(headerSection instanceof HTMLElement)) return 0;

  const top = parseFloat(getComputedStyle(headerSection).top);
  return Number.isFinite(top) ? top : 0;
}

/**
 * @returns {number}
 */
function getHeaderGroupBottomFromCss() {
  const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
  const stickyTopOffset = getStickyHeaderTopOffset();
  const height = Number.isFinite(headerHeight) ? headerHeight : 0;

  return height + stickyTopOffset;
}
